<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref, Ref, watch } from 'vue';
import {
  ShiftVector,
  RobotVisualizer,
} from '@therobotcompany/robot-visualizer';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import { useDisplay } from 'vuetify';
import gettext from '@/i18n/gettext';
import { MODULES_BASE_URL } from '@/Constants';
import { ModuleIdNameMapping, RobotFinderType } from '@/models/Robot';
import RobotFinderResult from '@/components/robotFinder/RobotFinderResult.vue';
import { IRobotFinderResult, ModuleFolderMapping } from '@/models/RobotFinder';
import RobotFinderService from '@/api/RobotFinderApi';
import {
  getModuleIdNameMapping,
  getModuleNamesAsCommaSeparatedString,
} from '@/helpers/RobotHelper';
import useAuthStore from '@/store/AuthStore';

interface Props {
  latestRobotModules: ModuleFolderMapping;
}

interface ModuleConfigurations {
  title: string;
  usecase: string;
  description: string;
  moduleIds: string[];
  jointAngles: number[];
  maxPayload: number;
  maxReach: number;
  dof: number;
  value: string;
}

const props = defineProps<Props>();
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();
const { $gettext } = gettext;
const { mdAndDown, smAndDown } = useDisplay();

const showSettings = ref(true);
const showGrid = ref(false);
const showAxesHelper = ref(true);
const environmentFileOrUrl: Ref<File | string | undefined> = ref(undefined);
const robotShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentScale: Ref<number> = ref(1);
const selectedCardIndex = ref<number | null>(null);
const hoveredCardIndex = ref<number | null>(null);
const payload = ref(0);
const reach = ref(0);
const generated = ref(false);
const createError = ref();

const moduleConfigurations: ComputedRef<ModuleConfigurations[]> = computed(
  () => {
    const robots = [
      {
        title: `3 ${$gettext('Degrees of Freedom')}`,
        usecase: '',
        description: $gettext('For simple tasks'),
        moduleIds: [
          '0346',
          '8008',
          '0301',
          '8008',
          '0317',
          '8007',
          '0300',
          '8007',
          '1000',
        ],
        jointAngles: [180, 30, -120, 0],
        maxPayload: 70,
        maxReach: 500,
        dof: 3,
        value: 'BD-D-L-D',
      },
      {
        title: `4 ${$gettext('Degrees of Freedom')}`,
        usecase: '',
        description: $gettext(
          'For palletizing cylinders/rotationally symmetric pieces',
        ),
        moduleIds: [
          '0100',
          '8002',
          '0086',
          '8002',
          '0079',
          '8001',
          '0085',
          '8000',
          '0074',
          '8000',
          '0085',
          '8000',
          '1000',
        ],
        jointAngles: [180, 30, -120, 27],
        maxPayload: 40,
        maxReach: 2200,
        dof: 4,
        value: 'BD-D-L-D-L-D',
      },
      {
        title: `5 ${$gettext('Degrees of Freedom')}`,
        usecase: $gettext('Palletizing'),
        description: $gettext(
          'For palletizing boxes when end rotation matters',
        ),
        moduleIds: [
          '0100',
          '8002',
          '0086',
          '8002',
          '0079',
          '8001',
          '0085',
          '8000',
          '0074',
          '8000',
          '0085',
          '8000',
          '0085',
          '8000',
          '1000',
        ],
        jointAngles: [180, 30, -120, 30, 0],
        maxPayload: 40,
        maxReach: 2300,
        dof: 5,
        value: 'BD-D-L-D-L-D-D',
      },
      {
        title: `5 ${$gettext('Degrees of Freedom')}`,
        usecase: $gettext('Assembly'),
        description: $gettext('For machine tending'),
        moduleIds: [
          '0100',
          '8002',
          '0086',
          '8002',
          '0079',
          '8001',
          '0085',
          '8000',
          '0087',
          '8000',
          '0008',
          '8000',
          '0087',
          '8000',
          '1000',
        ],
        jointAngles: [180, 30, -120, 0, 0],
        maxPayload: 40,
        maxReach: 2120,
        dof: 5,
        value: 'BD-D-L-D-D-I-D',
      },
      {
        title: `6 ${$gettext('Degrees of Freedom')}`,
        usecase: '',
        description: $gettext('For complex tasks'),
        moduleIds: [
          '0100',
          '8002',
          '0086',
          '8002',
          '0079',
          '8001',
          '0085',
          '8000',
          '0087',
          '8000',
          '0008',
          '8000',
          '0087',
          '8000',
          '0087',
          '8000',
          '1000',
        ],
        jointAngles: [180, 30, -120, 0, -23, 0],
        maxPayload: 35,
        maxReach: 2120,
        dof: 6,
        value: 'BD-D-L-D-D-I-D-D',
      },
      {
        title: `7 ${$gettext('Degrees of Freedom')}`,
        usecase: '',
        description: $gettext('For complex tasks'),
        moduleIds: [
          '0100',
          '8002',
          '0086',
          '8002',
          '0086',
          '8002',
          '0059',
          '8001',
          '0085',
          '8000',
          '0087',
          '8000',
          '0008',
          '8000',
          '0087',
          '8000',
          '0087',
          '8000',
          '1000',
        ],
        jointAngles: [180, 30, -180, -120, 0, -25, 0],
        maxPayload: 23,
        maxReach: 1850,
        dof: 7,
        value: 'BD-D-D-I-D-D-I-D-D',
      },
      // {
      //   title: `8 ${$gettext('Degrees of Freedom')}`,
      //   usecase: $gettext(''),
      //   description: $gettext('For complex tasks'),
      //   moduleIds: [
      //     '0100',
      //     '8002',
      //     '0086',
      //     '8002',
      //     '0079',
      //     '8001',
      //     '0085',
      //     '8000',
      //     '0074',
      //     '8000',
      //     '0085',
      //     '8000',
      //     '0012',
      //   ],
      //   jointAngles: [180, 30, -120, 27],
      //   maxPayload: 19.6,
      //   maxReach: 1920,
      //   dof: 8,
      //   value: 'BD-D-D-I-D-D-I-D-D-D',
      // },
    ];

    if (selectedCardIndex.value !== null) {
      return [robots[selectedCardIndex.value]];
    }
    return robots;
  },
);

const robotLoaded = ref<boolean[]>(
  Array(moduleConfigurations.value.length).fill(false),
);
const allLoaded = computed(() => robotLoaded.value.every((v) => v));
const currentAngles = ref<number[]>([]);
const increasing = ref(true);
const allResults = ref<IRobotFinderResult[]>([]);

const moduleIdNameMapping: ComputedRef<ModuleIdNameMapping[]> = computed(() => {
  return getModuleIdNameMapping(props.latestRobotModules);
});

async function getGeneratedRobotConfigurations() {
  const results: IRobotFinderResult[] = [];
  createError.value = null;
  try {
    const response =
      await RobotFinderService.getGeneratedRobotsForConfigurationString(
        moduleConfigurations.value[0].value,
        Math.round(payload.value * 10) / 10,
        Math.round(reach.value) / 1000,
      );
    response.forEach((robot: IRobotFinderResult) => {
      results.push({
        ...response,
        resultType: RobotFinderType.REVERSE_CALCULATION,
        module_names: getModuleNamesAsCommaSeparatedString(
          robot.module_ids,
          moduleIdNameMapping.value,
        ),
        module_ids: robot.module_ids,
        display_pose: robot.display_pose,
        payload: robot.payload,
        reach: robot.reach,
      });
    });
    if (response.length > 0) {
      generated.value = true;
    } else {
      generated.value = false;
      createError.value = $gettext(
        'We could not generate Robots that support your payload and reach requirements. Please change them and try again!',
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(`Failed`, err);
    generated.value = false;
    createError.value = $gettext(
      'We could not generate Robots that support your payload and reach requirements. Please change them and try again!',
    );
  }
  allResults.value = results;
}

onMounted(() => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
});

watch(allLoaded, (loaded) => {
  if (loaded) {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
});

watch(hoveredCardIndex, (newIndex) => {
  if (newIndex !== null) {
    const module = moduleConfigurations.value[newIndex];
    currentAngles.value = module.jointAngles;
    increasing.value = true;
  } else {
    currentAngles.value = [];
  }
});

const cardClickCallback = (index: number) => {
  if (selectedCardIndex.value === null) {
    selectedCardIndex.value = index;
  }
};

const generateRobots = async () => {
  await getGeneratedRobotConfigurations();
};

const goBack = () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  selectedCardIndex.value = null;
  generated.value = false;
  createError.value = null;
};
</script>

<template>
  <v-sheet v-show="allLoaded" color="transparent position-relative">
    <div>
      <h1 v-if="!generated && selectedCardIndex === null" class="ma-2">
        {{ $gettext('Select a configuration based on your use case') }}
      </h1>
      <h1 v-else-if="!generated && selectedCardIndex !== null" class="ma-2">
        {{
          $gettext(
            'Adjust reach and payload for your %{dof} Degrees of Freedom Robot',
            { dof: moduleConfigurations[0].dof.toString() },
          )
        }}
      </h1>
      <h1 v-else class="ma-2">
        {{
          $gettext('Your %{dof} Degrees of Freedom Robot suggestions:', {
            dof: moduleConfigurations[0].dof.toString(),
          })
        }}
      </h1>
    </div>

    <v-row class="mt-6">
      <v-col v-if="smAndDown && selectedCardIndex !== null" cols="12">
        <v-btn
          class="mt-4 ml-1"
          prepend-icon="mdi-keyboard-backspace"
          @click="goBack"
          >{{ $gettext('Go Back') }}</v-btn
        >
      </v-col>
      <v-col v-if="selectedCardIndex !== null" class="ml-2 mt-4">
        <v-row>
          <v-col cols="12">
            <v-slider
              v-model="payload"
              :label="$gettext('Payload')"
              class="mt-10 smart-suggestions-slider"
              :class="`smart-suggestions-slider--${authStore.language}`"
              append-icon="mdi-weight-kilogram"
              thumb-label="always"
              :max="moduleConfigurations[0]?.maxPayload"
              @touchstart.stop
              @touchmove.stop
              @touchend.stop
            >
              <template v-slot:thumb-label="{ modelValue }">
                {{ Math.round((modelValue * 10) / 10) }}kg
              </template>
            </v-slider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-slider
              v-model="reach"
              :label="$gettext('Reach')"
              class="smart-suggestions-slider"
              :class="`smart-suggestions-slider--${authStore.language}`"
              append-icon="mdi-arrow-expand-horizontal"
              thumb-label="always"
              :max="moduleConfigurations[0]?.maxReach"
              @touchstart.stop
              @touchmove.stop
              @touchend.stop
            >
              <template v-slot:thumb-label="{ modelValue }">
                {{ Math.round((modelValue * 10) / 10) }}mm
              </template>
            </v-slider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn
              class="glow bouncy-button mx-2"
              style="width: calc(100% - 24px)"
              prepend-icon="mdi-creation"
              @click="generateRobots"
            >
              {{ $gettext('Generate') }}</v-btn
            >
            <v-btn
              v-if="!smAndDown"
              class="position-fixed"
              style="bottom: 32px; left: 88px"
              prepend-icon="mdi-keyboard-backspace"
              @click="goBack"
            >
              {{ $gettext('Go Back') }}</v-btn
            >
          </v-col>
          <v-col cols="12">
            <v-alert v-if="createError" class="" type="error" outlined>
              {{ createError }}
            </v-alert>
          </v-col>
        </v-row>
      </v-col>
      <template v-if="!generated">
        <v-col
          v-for="(module, index) in moduleConfigurations"
          :key="index"
          class="mb-2 d-flex animate-col"
          cols="12"
          :sm="selectedCardIndex != null ? 8 : 6"
          :md="selectedCardIndex != null ? 9 : 4"
          :lg="selectedCardIndex != null ? 10 : 3"
        >
          <v-card
            class="card flex-grow-1 cursor-pointer"
            :ripple="false"
            :class="{ 'clicked-card': selectedCardIndex !== null }"
            style="width: 100%"
          >
            <div
              class="ma-2"
              style="outline: 8px solid white"
              :style="
                selectedCardIndex != null
                  ? 'height: calc(100vh - 350px)'
                  : 'height: 200px'
              "
            >
              <robot-visualizer
                v-model:environmentScale="environmentScale"
                :modulesBaseUrl="MODULES_BASE_URL"
                :moduleIds="module.moduleIds"
                :currentAngles="module.jointAngles"
                :showGrid="showGrid"
                :showAxesHelper="showAxesHelper"
                :tcpShift="[0, 0, 0, 0, 0, 0]"
                :showSettings="showSettings"
                :environmentShift="environmentShift"
                :robotShift="robotShift"
                backgroundColor="#fff"
                :environmentFileOrUrl="environmentFileOrUrl"
                :demoAnimationOnHover="true"
                @onRobotLoadedChanged="
                  (isLoaded: boolean) => (robotLoaded[index] = isLoaded)
                "
              />
            </div>
            <v-card-title
              style="white-space: normal; word-break: break-word"
              @click="cardClickCallback(index)"
              >{{ module.title }}</v-card-title
            >
            <v-card-text @click="cardClickCallback(index)">
              {{ module.description }}
            </v-card-text>
          </v-card>
        </v-col>
      </template>
      <template v-if="generated">
        <v-col class="my-2 d-flex" cols="12" sm="8" md="9" lg="10">
          <v-row>
            <v-col
              v-for="(entry, index) in allResults"
              :key="index"
              cols="12"
              :md="allResults.length === 1 ? 12 : 6"
              :lg="allResults.length === 1 ? 12 : 4"
            >
              <v-card class="h-100">
                <v-card-text>
                  <robot-finder-result
                    :data="entry"
                    :name="`Robot ${index + 1}`"
                    :show-in-columns="allResults.length !== 1 || mdAndDown"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </v-row>
  </v-sheet>
</template>

<style lang="scss" scoped>
.clicked-card {
  .v-card-title,
  .v-card-text {
    background-color: rgb(var(--v-theme-dusk-blue));
    color: white;
  }
}

.card:hover:not(.clicked-card) {
  .v-card-title,
  .v-card-text {
    background-color: rgb(var(--v-theme-dawn-blue));
    color: white;
  }
}

.smart-suggestions-slider--en {
  :deep(.v-input__prepend) {
    width: 72px;
  }
}

.smart-suggestions-slider--de {
  :deep(.v-input__prepend) {
    width: 92px;
  }
}
</style>
